import { useState, useEffect } from "react";
import axios from "axios";
import { Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BACKEND_URL } from "../config";

const API = `${BACKEND_URL}/api`;

const PushNotificationSetup = () => {
  const [permission, setPermission] = useState(() => {
    try {
      return typeof Notification !== 'undefined' ? Notification.permission : 'default';
    } catch {
      return 'default';
    }
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Solo ejecutar si Notification API está disponible
    if (typeof Notification !== 'undefined') {
      checkSubscriptionStatus();
    }
  }, []);

  const checkSubscriptionStatus = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
        // No hacer nada si falla, simplemente no mostrar como subscrito
        setIsSubscribed(false);
      }
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribeToPush = async () => {
    setLoading(true);
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        toast.error('Permiso de notificaciones denegado');
        setLoading(false);
        return;
      }

      // Register service worker
      if (!('serviceWorker' in navigator)) {
        toast.error('Service Worker no soportado en este navegador');
        setLoading(false);
        return;
      }

      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none' // Evitar problemas de cache
      });
      
      await navigator.serviceWorker.ready;
      console.log('Service Worker registered');

      // Get VAPID public key from backend
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('Push subscription:', subscription);

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, subscription.toJSON(), {
        withCredentials: true
      });

      setIsSubscribed(true);
      toast.success('¡Notificaciones activadas! Recibirás alertas en tu celular');
    } catch (error) {
      console.error('Error subscribing to push:', error);
      toast.error('Error al activar notificaciones: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await axios.post(`${API}/push/unsubscribe`, 
          { endpoint: subscription.endpoint },
          { withCredentials: true }
        );
        
        await subscription.unsubscribe();
        setIsSubscribed(false);
        toast.success('Notificaciones desactivadas');
      }
    } catch (error) {
      console.error('Error unsubscribing from push:', error);
      toast.error('Error al desactivar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  // Don't show if browser doesn't support
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {!isSubscribed ? (
        <Button
          onClick={subscribeToPush}
          disabled={loading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          title="Activar notificaciones push en tu celular"
        >
          <Bell className="h-4 w-4" />
          {loading ? 'Activando...' : 'Activar Push 📱'}
        </Button>
      ) : (
        <Button
          onClick={unsubscribeFromPush}
          disabled={loading}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-green-500 border-green-500"
          title="Notificaciones push activas"
        >
          <Bell className="h-4 w-4 animate-pulse" />
          Push Activo ✅
        </Button>
      )}
    </div>
  );
};

export default PushNotificationSetup;
